// ============================================
// Divergence Summary Generator
//
// Every 25 reviewed cases: analyze divergence
// patterns and draft a proposed prompt change.
// Stan approves → deploys to clinicalAnalysisPrompt.
//
// Collection: platform/calibration/divergence_summaries/{summaryId}
// ============================================

import {
  collection, doc, getDocs, setDoc, updateDoc,
  query, orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { claudeGenerate } from '../ai/providers/claude';
import { getActiveClinicalPrompt, saveClinicalPrompt } from '../firestore/clinicalPromptService';
import { getReviewedCases } from '../firestore/calibrationCaseService';
import type { DivergenceSummary, DivergencePattern, ClinicalAnalysisPrompt } from '../../types/labUpload';
import { C_PLATFORM, DOC_CALIBRATION, SC_DIVERGENCE_SUMMARIES } from '../../constants/collections';

// ── Collection ref ──

const summariesCol = () =>
  collection(db, C_PLATFORM, DOC_CALIBRATION, SC_DIVERGENCE_SUMMARIES);

// ── Core Functions ──

/**
 * Generate a divergence summary from reviewed cases.
 * Groups divergences by decision area and drafts a prompt change.
 */
export async function generateDivergenceSummary(
  brandId: string,
): Promise<DivergenceSummary> {
  const cases = await getReviewedCases(brandId);
  const currentPrompt = await getActiveClinicalPrompt();

  // Collect all divergences
  const divergencesByArea: Record<string, { count: number; notes: string[] }> = {};

  for (const c of cases) {
    if (!c.decisionPoints) continue;
    for (const dp of c.decisionPoints) {
      if (dp.alignment === 'delta' && dp.deltaNote) {
        if (!divergencesByArea[dp.area]) {
          divergencesByArea[dp.area] = { count: 0, notes: [] };
        }
        divergencesByArea[dp.area].count++;
        divergencesByArea[dp.area].notes.push(dp.deltaNote);
      }
    }
  }

  const patterns: DivergencePattern[] = Object.entries(divergencesByArea)
    .sort((a, b) => b[1].count - a[1].count)
    .map(([area, data]) => ({
      decisionArea: area,
      frequency: data.count,
      description: data.notes.slice(0, 3).join('; '),
    }));

  // Ask Claude to draft a prompt improvement
  const summaryText = patterns
    .map((p) => `- ${p.decisionArea} (${p.frequency}x): ${p.description}`)
    .join('\n');

  const proposedPromptChange = await claudeGenerate(
    'claude-sonnet-4-5-20250929',
    'You are a prompt engineer improving a clinical analysis system. Based on the divergence patterns between the AI model and a physician, suggest specific prompt modifications to reduce these divergences. Be precise about what to add, remove, or change in the system prompt.',
    `Current system prompt:\n${currentPrompt?.systemPrompt ?? '(not available)'}\n\nDivergence patterns from last ${cases.length} reviewed cases:\n${summaryText}\n\nDraft a specific prompt modification to address the top divergence patterns. Return the modification as clear instructions (e.g., "Add to the system prompt: ..." or "Change the section about ... to say ...").`,
    0.3,
    2048,
  );

  const summaryId = `div-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  // Compute correct batch range (each summary covers 25 cases)
  const existingSummaries = await getDivergenceSummaries();
  const batchStart = existingSummaries.length * 25 + 1;

  const summary: DivergenceSummary = {
    summaryId,
    batchRange: { start: batchStart, end: cases.length },
    patterns,
    proposedPromptChange,
    approvedByStan: false,
    createdAt: new Date().toISOString(),
  };

  await setDoc(doc(summariesCol(), summaryId), {
    ...summary,
    _updatedAt: Timestamp.now(),
  });

  return summary;
}

/**
 * Get all divergence summaries, most recent first.
 */
export async function getDivergenceSummaries(): Promise<DivergenceSummary[]> {
  const snap = await getDocs(
    query(summariesCol(), orderBy('createdAt', 'desc')),
  );
  return snap.docs.map((d) => d.data() as DivergenceSummary);
}

/**
 * Approve a divergence summary and deploy the prompt change.
 * Creates a new ClinicalAnalysisPrompt version.
 */
export async function approveDivergenceSummary(
  summaryId: string,
  updatedSystemPrompt: string,
  changeReason: string,
): Promise<void> {
  // Mark summary as approved
  await updateDoc(doc(summariesCol(), summaryId), {
    approvedByStan: true,
    appliedAt: new Date().toISOString(),
    _updatedAt: Timestamp.now(),
  });

  // Deploy new prompt version
  const currentPrompt = await getActiveClinicalPrompt();
  const newPrompt: ClinicalAnalysisPrompt = {
    versionId: `v-${Date.now()}`,
    systemPrompt: updatedSystemPrompt,
    userPromptTemplate: currentPrompt?.userPromptTemplate ?? '',
    isActive: true,
    createdAt: new Date().toISOString(),
    createdBy: 'stan',
    changeReason,
    parentVersionId: currentPrompt?.versionId,
  };

  await saveClinicalPrompt(newPrompt);
}

/**
 * Reject a divergence summary with reason.
 */
export async function rejectDivergenceSummary(
  summaryId: string,
  rejectionReason: string,
): Promise<void> {
  await updateDoc(doc(summariesCol(), summaryId), {
    approvedByStan: false,
    rejectionReason,
    _updatedAt: Timestamp.now(),
  });
}
